/**
 * Org members resource — typed wrappers over the team-management endpoints
 * (multi-tenancy Phase D). All are admin-only (the caller must be an admin
 * of the org); the backend asserts the path {org_id} equals the X-Org-Id
 * header, so pass the active org id as orgId.
 */

import { api } from "@/lib/api-client";
import type { Paginated } from "./types";

/** Mirrors orgs.Member. */
export interface Member {
  user_id: string;
  email: string;
  display_name: string;
  role: string;
  is_owner: boolean;
  created_at: string;
  /** The member's roles in the suite products other than Twin, by product key (migration 0227); absent from an
   *  older API. */
  product_roles?: Record<string, string>;
}

export const membersApi = {
  list: (orgId: string) =>
    api.get<Paginated<Member>>(`/v1/platform/orgs/${orgId}/members`),
  updateRole: (orgId: string, userId: string, role: string) =>
    api.patch<{ user_id: string; role: string }>(
      `/v1/platform/orgs/${orgId}/members/${userId}`,
      { role },
    ),
  remove: (orgId: string, userId: string) =>
    api.delete<void>(`/v1/platform/orgs/${orgId}/members/${userId}`),
  /** The member's role in a suite product other than Twin, which the org must hold. */
  setProductRole: (orgId: string, userId: string, product: string, role: string) =>
    api.put<{ user_id: string; product: string; role: string }>(
      `/v1/platform/orgs/${orgId}/members/${userId}/products/${product}`,
      { role },
    ),
  /** Takes the member's role in the product away; idempotent. */
  removeProductRole: (orgId: string, userId: string, product: string) =>
    api.delete<void>(`/v1/platform/orgs/${orgId}/members/${userId}/products/${product}`),
};
